/**
 * El borrador del compositor, fuera del componente.
 *
 * Existe por la sesión: si caduca y no se puede renovar, la aplicación vuelve
 * al SSO --una navegación de verdad, que desmonta todo-- y lo que la persona
 * estaba escribiendo no puede perderse en el viaje. En `sessionStorage`
 * porque es de esta pestaña y solo tiene que durar ese ir y volver.
 *
 * Es UNO POR CONVERSACIÓN, no uno global: cambiar de chat no debe arrastrar
 * lo que se estaba escribiendo en el anterior, y volver a él debe
 * recuperarlo. La conversación todavía sin id (antes del primer mensaje)
 * usa su propio hueco (`NEW_CONVERSATION`).
 *
 * Es una comodidad: si el almacenamiento no está, se escribe igual y
 * simplemente no se recupera.
 */
const PREFIX = "embajadores.chat.draft.";

/** Hueco para la conversación nueva, antes de que el backend le dé un id. */
const NEW_CONVERSATION = "new";

const keyFor = (conversationId: string | undefined): string =>
  `${PREFIX}${conversationId ?? NEW_CONVERSATION}`;

export const readDraft = (conversationId: string | undefined): string => {
  try {
    return sessionStorage.getItem(keyFor(conversationId)) ?? "";
  } catch {
    return "";
  }
};

export const saveDraft = (
  conversationId: string | undefined,
  text: string,
): void => {
  try {
    const key = keyFor(conversationId);
    if (text === "") sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, text);
  } catch {
    // Sin almacenamiento no hay borrador que guardar.
  }
};

/**
 * Devuelve a la caja de SU conversación un mensaje que no llegó a enviarse.
 * No pisa lo que la persona haya empezado a escribir después.
 */
export const restoreUnsent = (
  conversationId: string | undefined,
  text: string,
): void => {
  if (text.trim() !== "" && readDraft(conversationId).trim() === "")
    saveDraft(conversationId, text);
};

/**
 * Tira el borrador de esa conversación. Se llama al enviar, en el acto: si
 * esperara a un efecto del compositor, el que se monta a continuación --al
 * pasar del hilo vacío al hilo con mensajes se monta otro-- leería el texto
 * viejo y lo devolvería a la caja.
 */
export const clearDraft = (conversationId: string | undefined): void => {
  saveDraft(conversationId, "");
};
